import React, { useState } from "react";
import { CalendarMonth as TimeInLieuIcon } from "@mui/icons-material";
import { GridValidRowModel } from "@mui/x-data-grid";
import { useSnackbar } from "notistack";
import { endOfYear, startOfYear } from "date-fns";
import {
  PageTitle,
  DataGrid,
  FilterBar,
  useQueryPagination,
} from "../../components";
import { useResponsive } from "../../hooks";
import { UpdateTimeInLieuInput } from "../../API";
import { toIntervalString, useTimeInLieuLib } from "../../lib";
import { columns, hiddenColumnsMobile } from "./columns";
import { useActionModals } from "./Modals";
import { timeInLieusQuery } from "./utils";

const TEST_ID = "time-in-lieu-tracking";

export const TimeInLieuTracker = () => {
  const [employeeID, setEmployeeID] = useState("");
  const [dates, setDates] = useState<Interval | null>({
    start: startOfYear(new Date()),
    end: endOfYear(new Date()),
  });

  const { isMd } = useResponsive();

  const { showActionModal } = useActionModals();
  const { updateTimeInLieuRecord } = useTimeInLieuLib();
  const { enqueueSnackbar } = useSnackbar();
  const showSnackbar = (message: string, error = false) => {
    enqueueSnackbar(message, { variant: error ? "error" : "success" });
  };

  const { data, isError, refetch, isPending, pagination } = useQueryPagination(
    timeInLieusQuery({
      dates,
      id: employeeID,
    })
  );
  const handleRowUpdate = (row: GridValidRowModel) => {
    const { description, id, endDate, startDate } = row;
    const data = {
      description,
      id,
      endDate,
      startDate,
    } as UpdateTimeInLieuInput;
    updateTimeInLieuRecord(data, {
      onSuccess: () =>
        postAction("Time in Lieu request has been updated.", false),
      onError: () =>
        postAction(
          "Error updating Time in Lieu request. Please try again in a few seconds",
          true
        ),
    });
    return { ...data, id: row.id };
  };

  const postAction = (message: string, isError: boolean) => {
    showSnackbar(message, isError);
  };

  const fileName = `${
    dates ? `${toIntervalString(dates)} - ` : ""
  }${employeeID}TimeInLieu_Report`;

  return (
    <>
      <PageTitle
        icon={<TimeInLieuIcon />}
        title="TimeInLieu Tracker"
        testID={TEST_ID}
      />

      <FilterBar
        employee={{ value: employeeID, onChange: setEmployeeID }}
        dates={{
          value: dates,
          onChange: setDates,
          type: "range",
          inputFormat: "yyyy-MM-dd",
        }}
        testID="TimeInLieu"
      />

      <DataGrid
        columns={columns({ showActionModal })}
        data={data?.items || []}
        csvOptions={{ fileName }}
        pagination={pagination}
        emptyState={{
          title: "Time in Lieu Requests",
          description:
            "Time in Lieu requests that are pending will appear here for you to approve.",
          testID: TEST_ID,
        }}
        errorState={{
          isError,
          onClick: refetch,
        }}
        loading={isPending}
        sortModel={[
          {
            field: "dateTaken",
            sort: "asc",
          },
        ]}
        testID={TEST_ID}
        disableColumnMenu
        hideFooterPagination
        disableSelectionOnClick
        hideColumnSelector
        processRowUpdate={handleRowUpdate}
        columnVisibility={isMd ? hiddenColumnsMobile : {}}
      />
    </>
  );
};
